/* eslint-disable complexity --- complexity needed for modal */
// src/layout/AdminModalContainer.tsx
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouterState } from '@tanstack/react-router';

import type { RootState } from '@/store/rootReducer';
import { useAppDispatch } from '@/store';
import { closeModal } from '@/store/slices/ui/UISlice';
import AdminModal from '@/components/molecules/AdminModal/AdminModal';
import { fetchOrgAdmins } from '@/store/thunks/organization/fetchOrgAdmins';
import { createAdminThunk } from '@/store/thunks/administratorsForm/createAdminThunk';
import { updateAdminThunk } from '@/store/thunks/administratorsForm/updateAdminThunk';
import { fetchAdminInfoThunk } from '@/store/thunks/auth/fetchAdminInfoThunk';

const AdminModalContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  const modal = useSelector((s: RootState) => s.ui.modal);
  const isCreate = modal?.type === 'createAdmin';
  const isEdit   = modal?.type === 'editAdmin';
  const editingAdmin =
    isEdit && modal && 'admin' in modal ? (modal as any).admin : undefined;

  const authUser = useSelector((s: RootState) => s.auth.user);

  // ✅ Title & defaults
  const title = isCreate ? 'Create Administrator' : isEdit ? 'Edit Administrator' : '';

  // IMPORTANT: For create, pass *no* defaultValues so the form is empty.
  const defaultValues = useMemo(() => {
    if (isEdit && editingAdmin) {
      return {
        name: editingAdmin.name ?? '',
        email: editingAdmin.email ?? '',
        phone: editingAdmin.phone ?? '',
      };
    }
    // create -> undefined to avoid prefill
    return undefined;
  }, [isEdit, editingAdmin]);

  const handleClose = useCallback(() => { dispatch(closeModal()); }, [dispatch]);

  const handleSubmit = useCallback(
    async (values: { name: string; email: string; phone: string }) => {
      try {
        if (isCreate) {
          await dispatch(createAdminThunk(values)).unwrap();
        } else if (isEdit && editingAdmin?.id) {
          await dispatch(updateAdminThunk({ id: editingAdmin.id, data: values })).unwrap();

          // If editing self, refresh auth profile
          if (authUser?.id && editingAdmin.id === authUser.id) {
            void dispatch(fetchAdminInfoThunk({ id: authUser.id }));
          }
        }

        // Only refresh list if we're on the administrators page
        if (pathname.startsWith('/admin/administrators')) {
          await dispatch(fetchOrgAdmins());
        }

        dispatch(closeModal());
      } catch {
        // errors handled upstream
      }
    },
    [dispatch, isCreate, isEdit, editingAdmin?.id, authUser?.id, pathname]
  );

  if (!isCreate && !isEdit) return null;

  return (
    <AdminModal
      // Key ensures the form remounts when switching create <-> edit, preventing stale defaults
      key={`${isCreate ? 'create' : 'edit'}-${editingAdmin?.id ?? 'new'}`}
      open
      onClose={handleClose}
      title={title}
      submitText={isCreate ? 'Create' : 'Save Changes'}
      modalSize="md"
      defaultValues={defaultValues}  // undefined for create => empty form
      onSubmit={(vals) => { void handleSubmit(vals); }}
    />
  );
};

export default AdminModalContainer;
